import { IYoutubeScrapeResponseItem } from "@shared/lib/types/influencer";
import { Avatar, TableProps, Tag, Typography } from "@shared/ui";
import dayjs from "dayjs";
import { YoutubeVideoCell } from "../ui/YoutubeVideoCell";
import styles from "../ui/YoutubeSearchResultsTable.module.scss";
import {
  Clip,
  formatDuration,
  formatEngagement,
  Stat,
} from "./formatYoutubeSearchCell";

const { Text, Link } = Typography;

type YoutubeColumn = NonNullable<
  TableProps<IYoutubeScrapeResponseItem>["columns"]
>[number];

const columnsByKey: Record<keyof IYoutubeScrapeResponseItem, YoutubeColumn> = {
  trackedQuery: {
    title: "Tracked Query",
    dataIndex: "trackedQuery",
    width: 110,
    ellipsis: true,
    render: (value: string) => <Tag>{value}</Tag>,
  },
  channelProfilePicUrl: {
    title: "Photo",
    dataIndex: "channelProfilePicUrl",
    width: 52,
    render: (value: string, record) => (
      <Avatar src={value} size={24}>
        {record.channelName?.[0]}
      </Avatar>
    ),
  },
  channelName: {
    title: "Channel Name",
    dataIndex: "channelName",
    width: 130,
    ellipsis: true,
    render: (value: string, record) => (
      <Link
        href={record.channelUrl}
        target="_blank"
        rel="noreferrer"
        className={styles.clipText}
      >
        {value}
      </Link>
    ),
  },
  channelId: {
    title: "Channel ID",
    dataIndex: "channelId",
    width: 110,
    ellipsis: true,
    render: (value: string) => <Clip value={value} />,
  },
  channelUrl: {
    title: "Channel URL",
    dataIndex: "channelUrl",
    width: 140,
    ellipsis: true,
    render: (value: string) =>
      value ? (
        <Link
          href={value}
          target="_blank"
          rel="noreferrer"
          className={styles.clipText}
        >
          {value}
        </Link>
      ) : (
        "—"
      ),
  },
  channelSubscribers: {
    title: "Subscribers",
    dataIndex: "channelSubscribers",
    width: 90,
    render: (value: number) => <Stat value={value} />,
  },
  videoUrl: {
    title: "Video",
    dataIndex: "videoUrl",
    width: 140,
    render: (value: string) => <YoutubeVideoCell videoUrl={value} />,
  },
  videoTitle: {
    title: "Video Title",
    dataIndex: "videoTitle",
    width: 180,
    ellipsis: true,
    render: (value: string) => <Clip value={value} />,
  },
  videoType: {
    title: "Type",
    dataIndex: "videoType",
    width: 80,
    ellipsis: true,
    render: (value: string) => value || "—",
  },
  durationSeconds: {
    title: "Duration",
    dataIndex: "durationSeconds",
    width: 80,
    render: (value: number) => formatDuration(value),
  },
  views: {
    title: "Views",
    dataIndex: "views",
    width: 80,
    render: (value: number) => <Stat value={value} />,
  },
  likes: {
    title: "Likes",
    dataIndex: "likes",
    width: 80,
    render: (value: number) => <Stat value={value} />,
  },
  comments: {
    title: "Comments",
    dataIndex: "comments",
    width: 90,
    render: (value: number) => <Stat value={value} />,
  },
  engagementRate: {
    title: "Engagement",
    dataIndex: "engagementRate",
    width: 100,
    render: (value: number) => (
      <Text className={styles.statValue}>{formatEngagement(value)}</Text>
    ),
  },
  publishedAt: {
    title: "Published",
    dataIndex: "publishedAt",
    width: 110,
    render: (value: string) =>
      value ? dayjs(value).format("MMM D, YYYY") : "—",
  },
  tags: {
    title: "Tags",
    dataIndex: "tags",
    width: 150,
    render: (value: string[]) => {
      const tags = value ?? [];
      const visible = tags.slice(0, 2);
      const extra = tags.length - visible.length;
      if (tags.length === 0) {
        return "—";
      }
      return (
        <div className={styles.tags}>
          {visible.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
          {extra > 0 ? <Tag>+{extra}</Tag> : null}
        </div>
      );
    },
  },
  fullDescription: {
    title: "Description",
    dataIndex: "fullDescription",
    width: 180,
    ellipsis: true,
    render: (value: string) => <Clip value={value} />,
  },
};

export function getYoutubeSearchColumns(): YoutubeColumn[] {
  return Object.values(columnsByKey);
}
